import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Alert,
    IconButton,
    Box,
    TableContainer,
    CircularProgress,
    Paper,
    Table,
    TableHead,
    TableRow,
    TableCell,
    Chip,
    Button,
    TableBody,
    Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { url } from 'helpers/url_helper';
import { getAssessmentResultOfBatchStudent } from 'helpers/batch_helper';
import Loader from 'components/Loader/Loader';

export const ExamResultsModal = ({ student, batchId, open, onClose }) => {
    const [results, setResults] = useState([]);
    const [assessmentResult, setAssessmentResult] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchAssessmentResult = async () => {
            try {
                setLoading(true);
                const data = await getAssessmentResultOfBatchStudent(batchId + "/" + student.id);
                if (data.success) {
                    setAssessmentResult(data);
                }
            } catch (error) {
                console.log(error);
                toast.error(error?.response?.data?.message || "Something went wrong");
            } finally {
                setLoading(false);
            }
        };

        const fetchResults = async () => {
            try {
                const response = await axios.get(`${url}batch/${batchId}/student/${student.id}/results`);

                if (response.data.success) {
                    setResults(response.data.result);
                } else {
                    setError(response.data.message);
                    toast.error(response.data.message);
                }
            } catch (err) {
                const errorMessage = err.response?.data?.message || 'Failed to fetch exam results';
                setError(errorMessage);
                toast.error(errorMessage);
            } finally {
                setLoading(false);
            }
        };

        if (open && student) {
            setLoading(true);
            Promise.all([fetchResults(), fetchAssessmentResult()])
                .finally(() => setLoading(false));
        }
    }, [open, student, batchId]);

    const arrayToDate = (dateArray) => {
        if (!dateArray || dateArray.length < 6) return null;
        return new Date(
            dateArray[0],        // year
            dateArray[1] - 1,    // month (0-indexed)
            dateArray[2],        // day
            dateArray[3],        // hours
            dateArray[4],        // minutes
            dateArray[5]         // seconds
        );
    };

    const calculateDuration = (startArray, endArray) => {
        const start = arrayToDate(startArray);
        const end = arrayToDate(endArray);

        if (!start || !end) return 'N/A';

        const diffInMs = end - start;
        const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
        const diffInSeconds = Math.floor((diffInMs % (1000 * 60)) / 1000);

        return `${diffInMinutes}m ${diffInSeconds}s`;
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            sx={{
                '& .MuiDialog-container': {
                    alignItems: 'center',
                    justifyContent: 'center',
                },
                '& .MuiPaper-root': {
                    height: '70vh',
                    maxHeight: '80vh',
                    width: '100%',
                    maxWidth: 'lg',
                }
            }}
        >
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                    Results for {student?.firstName} {student?.lastName}

                </Box>
                <IconButton
                    aria-label="close"
                    onClick={() => { onClose(); setResults([]); setAssessmentResult({}); }}
                    sx={{
                        color: (theme) => theme.palette.grey[500],
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers sx={{ overflow: 'auto' }}>
                {loading ? (
                    <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                        {/* <CircularProgress /> */}
                        <Loader />
                    </Box>
                ) : error ? (
                    <Alert severity="error">{error}</Alert>
                ) : (
                    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {(!assessmentResult?.result && (!results || results.length === 0)) ? (
                            <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                                <Typography variant="h6" color="textSecondary">
                                    Data not found
                                </Typography>
                            </Box>
                        ) : (
                            <>
                                {/* Assessment Results Section */}
                                {assessmentResult?.result && (
                                    <Box>
                                        <Typography variant="h6" gutterBottom>
                                            Assessment Results
                                        </Typography>
                                        {assessmentResult?.percent !== null && (
                                            <Typography variant="subtitle1" sx={{ mt: 1 }}>
                                                Overall Assessment Percentage: <strong>{assessmentResult?.percent}%</strong>
                                            </Typography>
                                        )}
                                        <TableContainer component={Paper} sx={{ height: 'calc(30vh - 48px)' }}>
                                            <Table stickyHeader>
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell>Total Marks</TableCell>
                                                        <TableCell>Attempted</TableCell>
                                                        <TableCell>Total Questions</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {assessmentResult.result.map((result, index) => (
                                                        <TableRow key={index}>
                                                            <TableCell>{result.totalMarks || 'N/A'}</TableCell>
                                                            <TableCell>
                                                                <Chip
                                                                    label={result.attempted ? 'Yes' : 'No'}
                                                                    color={result.attempted ? 'success' : 'error'}
                                                                    size="small"
                                                                />
                                                            </TableCell>
                                                            <TableCell>{result.totalQuestions || 'N/A'}</TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </Box>
                                )}

                                {/* Exam Results Section */}
                                {results?.length > 0 && (
                                    <Box >
                                        <Typography variant="h6" gutterBottom>
                                            Exam Results
                                        </Typography>
                                        <TableContainer component={Paper} sx={{ height: 'calc(30vh - 48px)' }}>
                                            <Table stickyHeader>
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell>Exam Title</TableCell>
                                                        <TableCell>Status</TableCell>
                                                        <TableCell>Score</TableCell>
                                                        <TableCell>Warnings</TableCell>
                                                        <TableCell>Duration</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {results.map((result) => (
                                                        <TableRow key={result.examId}>
                                                            <TableCell>{result.examTitle}</TableCell>
                                                            <TableCell>
                                                                <Chip
                                                                    label={result.submitted ? 'Submitted' : 'Pending'}
                                                                    color={result.submitted ? 'success' : 'warning'}
                                                                    size="small"
                                                                />
                                                            </TableCell>
                                                            <TableCell>{result.score}</TableCell>
                                                            <TableCell>
                                                                <Chip
                                                                    label={result.warningCount}
                                                                    color={result.warningCount > 0 ? 'error' : 'default'}
                                                                    size="small"
                                                                />
                                                            </TableCell>
                                                            <TableCell>
                                                                {calculateDuration(result.startTime, result.endTime)}
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </Box>
                                )}
                            </>
                        )}
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={() => { onClose(); setResults([]); setAssessmentResult({}); }}>Close</Button>
            </DialogActions>
        </Dialog>
    );
};